/**
 * CORRECT THE LINE (a tester's report, 2026-10-14). A line Leveri could not read is shown column by column, each cell
 * under the record's own heading, as the server found it in the record — never as the screen guesses it. The person
 * corrects the cell that is wrong and sends the line; Leveri files it as evidence in their name and the request is
 * answered through the same gate as any answer. The record itself is not changed.
 */
import { useState } from "react";
import { WorkActionResponse, type OpenRequest, type WorkFile } from "@leveri/contracts";
import { Button, Notice, Reading, TextField } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

export interface CorrectLineProps {
  workId: string;
  request: OpenRequest;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function CorrectLine({ workId, request, onWork, onOutcome }: CorrectLineProps) {
  const line = request.correctableLine;
  const [cells, setCells] = useState<string[]>(() => [...(line?.cells ?? [])]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();
  if (!line) return null;
  const changed = cells.some((cell, i) => cell.trim() !== (line.cells[i] ?? ""));

  async function send() {
    if (busy || !changed) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}/corrected-line`, WorkActionResponse, {
        cells: cells.map((cell) => cell.trim()),
        // One correction per request: pressing the button twice is one act.
        submissionKey: keyFor(`corrected-line:${request.requirementId}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-rule-soft pt-4">
      <p className="text-[13px] text-stone">
        The line as the record prints it, column by column. Correct the cell that is wrong — a date with its year, an
        amount as figures — and send it. Leveri files the corrected line in your name; the record itself is not changed.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {line.headings.map((heading, i) => (
          <TextField
            key={`${i}:${heading}`}
            label={heading}
            value={cells[i] ?? ""}
            disabled={busy}
            onChange={(event) => setCells((now) => now.map((cell, j) => (j === i ? event.target.value : cell)))}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3.5">
        <Button variant="secondary" disabled={busy || !changed} onClick={() => void send()}>Send the corrected line</Button>
        {!changed && <span className="text-[13px] text-stone">Change the cell that is wrong first.</span>}
      </div>
      {busy && <div className="mt-3"><Reading>Filing the corrected line, and carrying on with the work…</Reading></div>}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </div>
  );
}
